import { getCountdownVisibleTimeframe } from '../../../lotteries/utils/getCountdownVisibleTimeframe/getCountdownVisibleTimeframe';
import type { LotteryName } from '../../../lottery-purchases/types';
import { getLiveDrawInProgressDuration } from '../getLiveDrawInProgressDuration/getLiveDrawInProgressDuration';

type ResultsDrawStatus = 'numbers' | 'draw_closes_in' | 'live_draw_in' | 'live_draw_in_progress' | 'awaiting_results';

type GetResultsDrawStatusParams = {
  now: number;
  closingDate: number;
  drawingDate: number;
  lotteryName: LotteryName;
  /** When true, a finished live window returns numbers instead of the spinner. */
  hasResult?: boolean;
};

function isLiveDrawInProgress(lotteryName: LotteryName, drawingDate: number | undefined, now: number) {
  const inProgressMs = getLiveDrawInProgressDuration(lotteryName);
  if (drawingDate == null || inProgressMs == null) {
    return false;
  }

  return now >= drawingDate && now < drawingDate + inProgressMs;
}

/** The live sequence has finished and published numbers can still be missing. */
function isPastLiveDrawWindow(lotteryName: LotteryName, drawingDate: number | undefined, now: number) {
  const inProgressMs = getLiveDrawInProgressDuration(lotteryName);
  if (drawingDate == null || inProgressMs == null) {
    return false;
  }

  return now >= drawingDate + inProgressMs;
}

function getResultsDrawStatus({ now, closingDate, drawingDate, lotteryName, hasResult = false }: GetResultsDrawStatusParams): ResultsDrawStatus {
  if (isLiveDrawInProgress(lotteryName, drawingDate, now)) {
    return 'live_draw_in_progress';
  }

  if (!hasResult && isPastLiveDrawWindow(lotteryName, drawingDate, now)) {
    return 'awaiting_results';
  }

  if (now >= closingDate && now < drawingDate) {
    return 'live_draw_in';
  }

  const remainingUntilClose = closingDate - now;
  const drawClosesInMs = getCountdownVisibleTimeframe(lotteryName);
  if (drawClosesInMs != null && remainingUntilClose > 0 && remainingUntilClose <= drawClosesInMs) {
    return 'draw_closes_in';
  }

  return 'numbers';
}

export { getResultsDrawStatus, isLiveDrawInProgress, isPastLiveDrawWindow, type ResultsDrawStatus };
